import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Gauge, Smartphone, Monitor } from 'lucide-react';

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      id: 'premier-medical',
      industry: 'Healthcare',
      title: 'Premier Family Medical',
      subtitle: 'Complete Website Redesign & Performance Optimization',
      image: 'https://images.pexels.com/photos/7789603/pexels-photo-7789603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      problem: 'Premier Family Medical had an outdated website that wasn\'t mobile-friendly. The slow loading times and poor user experience were causing visitors to leave before booking appointments.',
      strategy: [
        'Complete website redesign with modern, clean aesthetics',
        'Mobile-first responsive design for seamless experience across devices',
        'Performance optimization reducing load times significantly',
        'Intuitive appointment booking system integration',
        'Streamlined user journey from landing to conversion'
      ],
      results: [
        { metric: '85%', label: 'Increase in Conversions' },
        { metric: '4.2s → 1.1s', label: 'Page Load Time' },
        { metric: '156%', label: 'More Mobile Users' },
        { metric: '92', label: 'PageSpeed Score' }
      ],
      testimonial: {
        quote: 'Our new website completely transformed how patients interact with our practice. The modern design and fast loading times have made a huge difference in user engagement.',
        author: 'Dr. Sarah Chen',
        role: 'Founder, Premier Family Medical'
      },
      keywords: ['medical website design', 'healthcare web development', 'responsive design', 'performance optimization']
    },
    {
      id: 'summit-construction',
      industry: 'Construction',
      title: 'Summit Construction Co.',
      subtitle: 'Portfolio Website with Project Showcase',
      image: 'https://images.pexels.com/photos/33404248/pexels-photo-33404248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      problem: 'Summit Construction needed a professional website that showcased their portfolio of projects and built credibility with potential clients in a competitive market.',
      strategy: [
        'Custom portfolio design with high-quality project galleries',
        'Case study pages highlighting project details and outcomes',
        'Mobile-optimized experience for on-site viewing',
        'Contact form integration for quote requests',
        'Trust elements including certifications and testimonials'
      ],
      results: [
        { metric: '67%', label: 'Increase in Quote Requests' },
        { metric: '45%', label: 'Lower Bounce Rate' },
        { metric: '3.2min', label: 'Avg. Session Duration' },
        { metric: '89%', label: 'Mobile Usability Score' }
      ],
      testimonial: {
        quote: 'The portfolio showcase really lets our work speak for itself. We\'re getting more qualified inquiries than ever before.',
        author: 'Mike Thompson',
        role: 'Owner, Summit Construction Co.'
      },
      keywords: ['construction website', 'portfolio website design', 'project showcase', 'custom web development']
    },
    {
      id: 'bella-cucina',
      industry: 'Restaurant',
      title: 'Bella Cucina Restaurant',
      subtitle: 'Restaurant Website with Online Reservations',
      image: 'https://images.pexels.com/photos/3296547/pexels-photo-3296547.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      problem: 'Bella Cucina needed a website that captured the ambiance of their restaurant and made it easy for customers to view menus and make reservations online.',
      strategy: [
        'Visually stunning design reflecting the restaurant\'s premium atmosphere',
        'Interactive menu with beautiful food photography',
        'Seamless online reservation system integration',
        'Mobile-first design for customers on-the-go',
        'Fast loading images optimized for web performance'
      ],
      results: [
        { metric: '120%', label: 'More Online Reservations' },
        { metric: '34%', label: 'Increase in Table Bookings' },
        { metric: '2.1s', label: 'Average Load Time' },
        { metric: '94', label: 'Mobile Performance Score' }
      ],
      testimonial: {
        quote: 'The new website perfectly captures the feel of our restaurant. Customers love being able to browse our menu and book tables so easily.',
        author: 'Maria Rossi',
        role: 'Owner, Bella Cucina Restaurant'
      },
      keywords: ['restaurant website design', 'online reservation system', 'food photography', 'hospitality web design']
    },
    {
      id: 'greenleaf-legal',
      industry: 'Legal',
      title: 'Greenleaf Legal Associates',
      subtitle: 'Professional Law Firm Website Redesign',
      image: 'https://images.unsplash.com/photo-1686061594225-3e92c0cd51b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxTRU8lMjBzZWFyY2glMjBlbmdpbmUlMjBvcHRpbWl6YXRpb24lMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc1MTc2MjI5fDA&ixlib=rb-4.1.0&q=85',
      problem: 'Greenleaf Legal needed a website that conveyed professionalism and trust while making it easy for potential clients to understand their services and get in touch.',
      strategy: [
        'Clean, professional design conveying trust and expertise',
        'Clear service pages with detailed practice area information',
        'Easy-to-use contact and consultation request forms',
        'Mobile-responsive design for accessibility',
        'Performance optimization for fast loading'
      ],
      results: [
        { metric: '78%', label: 'More Consultation Requests' },
        { metric: '62%', label: 'Improvement in Page Speed' },
        { metric: '4.5min', label: 'Avg. Time on Site' },
        { metric: '95', label: 'Accessibility Score' }
      ],
      testimonial: {
        quote: 'Our new website perfectly represents the professionalism of our firm. The clear layout helps potential clients understand exactly how we can help them.',
        author: 'James Greenleaf',
        role: 'Managing Partner, Greenleaf Legal Associates'
      },
      keywords: ['law firm website', 'legal website design', 'professional services web development', 'attorney website']
    }
  ];

  return (
    <div data-testid="case-studies-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="overline">Case Studies</span>
            <h1 className="heading-hero text-[#F5F5F7] mt-4 mb-6">
              Our Work.
              <br />
              <span className="text-gradient-gold">Real Results.</span>
            </h1>
            <p className="body-large">
              See how we've helped businesses across industries with custom 
              website design and development that delivers results.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {caseStudies.map((study, index) => (
        <section 
          key={study.id}
          id={study.id}
          data-testid={`case-study-${study.id}`}
          className={`section-padding ${index % 2 === 0 ? 'bg-[#050505]' : 'bg-[#0a0a0a]'}`}
        >
          <div className="container-custom">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="text-[#D4AF37] text-sm font-medium px-3 py-1 border border-[#D4AF37]/30">
                {study.industry}
              </span>
              <span className="text-[#71717A] text-sm">Case Study #{index + 1}</span>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Image */}
              <div className="relative">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={study.image} 
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
                </div>
                
                {/* Results Cards Overlay */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {study.results.map((result, i) => (
                    <div key={i} className="bg-[#111111] border border-white/10 p-4 text-center">
                      <div className="text-2xl font-bold text-[#D4AF37]">{result.metric}</div>
                      <div className="text-xs text-[#71717A] mt-1">{result.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#F5F5F7] mb-2">
                  {study.title}
                </h2>
                <p className="text-[#D4AF37] text-lg mb-8">{study.subtitle}</p>

                {/* Problem */}
                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-wider text-[#71717A] mb-3">The Challenge</h3>
                  <p className="text-[#A1A1AA] leading-relaxed">{study.problem}</p>
                </div>

                {/* Strategy */}
                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-wider text-[#71717A] mb-3">Our Solution</h3>
                  <ul className="space-y-2">
                    {study.strategy.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                        <span className="text-[#A1A1AA] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Testimonial */}
                <div className="bg-[#111111] border-l-2 border-[#D4AF37] p-6">
                  <p className="text-[#F5F5F7] italic mb-4">"{study.testimonial.quote}"</p>
                  <div>
                    <p className="text-[#D4AF37] font-medium">{study.testimonial.author}</p>
                    <p className="text-[#71717A] text-sm">{study.testimonial.role}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Results Summary */}
      <section className="py-20 bg-[#111111]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="overline">By The Numbers</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4">
              What We Consistently Deliver
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Globe, value: '150+', label: 'Websites Delivered' },
              { icon: Gauge, value: '90+', label: 'Avg. Performance Score' },
              { icon: Smartphone, value: '100%', label: 'Mobile Responsive' },
              { icon: Monitor, value: '98%', label: 'Client Satisfaction' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">{stat.value}</div>
                <div className="text-[#71717A] text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="overline">Industries</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4">
              Who We Build For
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'Healthcare & Medical',
              'Construction & Trades',
              'Restaurants & Hospitality',
              'Legal & Professional',
              'Real Estate',
              'E-Commerce & Retail',
              'Fitness & Wellness',
              'Local Services'
            ].map((industry, i) => (
              <div key={i} className="bg-[#111111] border border-white/10 p-4 text-center hover:border-[#D4AF37]/50 transition-colors">
                <span className="text-[#A1A1AA] text-sm">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline">Your Project Could Be Next</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
              Ready to Build Your Website?
            </h2>
            <p className="body-large mb-10">
              Let's discuss how we can create a high-performance website 
              tailored to your business needs.
            </p>
            <Link
              to="/contact"
              data-testid="case-studies-cta-button"
              className="btn-primary inline-flex"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
